import { useSyncExternalStore } from "react";

// Tracks the revision id of the JS bundle the runtime is *actually* running, as
// reported by Metro itself — not a value we compute, so it's a trustworthy "is
// this the latest bundle?" signal.
//
// Metro stamps every bundle revision with an id (the same value it returns in the
// `X-Metro-Delta-ID` response header) and ships it to the device over the HMR
// WebSocket: each `update-start` / `update` / `update-done` message body carries a
// `revisionId`. On (re)connect — including after a full reload — Metro sends an
// initial `update` (`isInitialUpdate: true`) carrying the current revision, so the
// id populates as soon as the bundle loads and changes again on every Fast Refresh.
//
// RN's `Libraries/Utilities/HMRClient` owns the single `MetroHMRClient` instance but
// keeps it module-private, so we can't read it directly. Instead we patch the shared
// `MetroHMRClient.prototype.emit` once: that one class backs every HMR connection, so
// observing its emitted bodies reliably captures the running bundle's revision id
// regardless of who created the client.
//
// Dev-only. In production `__DEV__` is false, `useBundleRevisionId` returns `null`,
// and nothing is patched.

type Listener = () => void;

// Each HMR message body carries `revisionId` (see metro-runtime's HMRClient). Read it
// from the `unknown` event arg without a type assertion: guard that the arg is an object
// carrying the key, which narrows it enough to read the field as `unknown`.
function readRevisionId(body: unknown): unknown {
  if (body == null || typeof body !== "object" || !("revisionId" in body)) {
    return undefined;
  }
  return body.revisionId;
}

type MetroHmrClientModule = {
  prototype: {
    emit: (event: string, ...args: unknown[]) => unknown;
  };
};

// Mirrors the messages whose bodies carry a `revisionId` (see metro-runtime's
// HMRClient `_ws.onmessage`).
const REVISION_BEARING_EVENTS = new Set(["update-start", "update", "update-done"]);

let bundleRevisionId: string | null = null;
const listeners = new Set<Listener>();
let installed = false;

function setRevisionId(next: string) {
  if (next === bundleRevisionId) {
    return;
  }
  bundleRevisionId = next;
  for (const listener of listeners) {
    listener();
  }
}

function readMetroHmrClientModule(): MetroHmrClientModule | null {
  // The same module path RN's HMRClient requires; resolving it here reuses the
  // single cached instance rather than loading a second copy.
  let candidate: unknown;
  try {
    // Must reuse RN's single cached MetroHMRClient instance; a static `import` would
    // eagerly load (and bundle) a second copy in production where the HMR client never runs.
    // eslint-disable-next-line typescript/no-require-imports -- see above
    candidate = require("metro-runtime/src/modules/HMRClient");
  } catch {
    return null;
  }
  if (typeof candidate !== "function") {
    return null;
  }
  const proto: unknown = (candidate as { prototype?: unknown }).prototype;
  if (proto == null || typeof proto !== "object") {
    return null;
  }
  if (typeof (proto as { emit?: unknown }).emit !== "function") {
    return null;
  }
  return candidate as MetroHmrClientModule;
}

function installHmrHook() {
  if (installed || !__DEV__) {
    return;
  }
  installed = true;

  const metroHmrClient = readMetroHmrClientModule();
  // Metro HMR client not present (e.g. a release-style bundle in dev) — leave the
  // build-time bundle stamp as the only signal.
  if (metroHmrClient == null) {
    return;
  }

  const originalEmit = metroHmrClient.prototype.emit;
  metroHmrClient.prototype.emit = function patchedEmit(
    this: unknown,
    event: string,
    ...args: unknown[]
  ) {
    if (REVISION_BEARING_EVENTS.has(event)) {
      const revisionId = readRevisionId(args[0]);
      if (typeof revisionId === "string" && revisionId !== "") {
        setRevisionId(revisionId);
      }
    }
    return originalEmit.call(this, event, ...args);
  };

  // Un-patch when *this* module is Fast-Refreshed. The patch lives on the shared
  // `MetroHMRClient.prototype`, but `patchedEmit` closes over this module instance
  // (`readRevisionId`, `setRevisionId`, …). After a Fast Refresh those bindings belong
  // to a disposed module, and calling the stale closure throws
  // `ReferenceError: Property 'readRevisionId' doesn't exist`. Restoring `originalEmit`
  // and clearing `installed` on dispose lets the fresh module re-install a closure over
  // its own (live) bindings.
  onHotDispose(() => {
    metroHmrClient.prototype.emit = originalEmit;
    installed = false;
  });
}

// Registers a Metro Fast Refresh dispose callback for this module, if HMR is active.
// `module.hot` is Metro's HMR API; read it through a guard so there's no unsafe assertion
// and no crash in a non-HMR (production) bundle where `module.hot` is absent.
function onHotDispose(callback: () => void): void {
  if (typeof module === "undefined") {
    return;
  }
  const hot: unknown = (module as { hot?: unknown }).hot;
  if (
    hot == null ||
    typeof hot !== "object" ||
    !("dispose" in hot) ||
    typeof hot.dispose !== "function"
  ) {
    return;
  }
  hot.dispose(callback);
}

function subscribe(listener: Listener): () => void {
  installHmrHook();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string | null {
  return bundleRevisionId;
}

// Subscribe as soon as the diagnostics module is evaluated, before React mounts the footer.
// eslint-disable-next-line no-top/no-top-level-side-effects -- dev-only HMR observer must precede React subscription
installHmrHook();

/**
 * Returns the revision id of the JS bundle the runtime is currently running (as
 * reported by Metro over HMR), or `null` if it hasn't been observed yet this
 * session (or in production). Shorten for display — the full id is a hex string.
 */
export function useBundleRevisionId(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
